/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { LiveActivityTaskReq } from './live-activity-task-req'
import { LiveActivityTaskRes } from './live-activity-task-res'
import { LoadLiveActivityTaskQuery } from './load-live-activity-task-query'
import { ExportLiveTaskReflectionsQuery } from './export-live-task-reflections-query'

const buildConfig = createConfigBuilder('growp')

export const liveApi = {
  createLiveActivityTaskConfig(
    liveActivityTaskReq: LiveActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/live/activity-tasks',
      {},
      null,
      liveActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          startAt: (v: any) => v || null,
          endAt: (v: any) => v || null,
          timedStartAt: (v: any) => v || null,
          prevTaskIds: (v: any) => v || [],
          isShow: (v: any) => v || false,
          adminIds: (v: any) => v || [],
          setting: (v: any) => v || null,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】直播-创建活动任务
   * @param liveActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  createLiveActivityTask(
    liveActivityTaskReq: LiveActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      liveApi.createLiveActivityTaskConfig(liveActivityTaskReq, options)
    ) as any
  },

  useCreateLiveActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: LiveActivityTaskReq) => liveApi.createLiveActivityTask(payload),
      mutationOptions
    )
  },

  updateLiveActivityTaskConfig(
    id: number,
    liveActivityTaskReq: LiveActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/live/activity-tasks/{id}',
      {
        id,
      },
      null,
      liveActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          startAt: (v: any) => v || null,
          endAt: (v: any) => v || null,
          timedStartAt: (v: any) => v || null,
          prevTaskIds: (v: any) => v || [],
          isShow: (v: any) => v || false,
          adminIds: (v: any) => v || [],
          setting: (v: any) => v || null,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】公告-编辑活动任务
   * @param id
   * @param liveActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  updateLiveActivityTask(
    id: number,
    liveActivityTaskReq: LiveActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      liveApi.updateLiveActivityTaskConfig(id, liveActivityTaskReq, options)
    ) as any
  },

  useUpdateLiveActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: LiveActivityTaskReq }) =>
        liveApi.updateLiveActivityTask(id, payload),
      mutationOptions
    )
  },

  loadLiveActivityTaskConfig(
    id: number,
    loadLiveActivityTaskQuery?: LoadLiveActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/live/activity-tasks/{id}',
      {
        id,
      },
      loadLiveActivityTaskQuery
    )
  },

  /**
   * @summary 【前/后台】公告-详情
   * @param id
   * @param [loadLiveActivityTaskQuery]
   * @param [loadLiveActivityTaskQuery.isForeground] 是否是前台请求
   * @param [options]
   * @return AxiosResponsePromise<LiveActivityTaskRes>
   */
  loadLiveActivityTask(
    id: number,
    loadLiveActivityTaskQuery?: LoadLiveActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<LiveActivityTaskRes> {
    return axios(
      liveApi.loadLiveActivityTaskConfig(id, loadLiveActivityTaskQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new LiveActivityTaskRes(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadLiveActivityTask(
    {
      id,
      query,
    }: {
      id: number
      query?: LoadLiveActivityTaskQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<LiveActivityTaskRes> {
    return useQueryApi<LiveActivityTaskRes>(
      liveApi.loadLiveActivityTask,
      'Live.loadLiveActivityTask',
      queryOptions,
      id,
      query
    )
  },

  completeLiveActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/live/activity-tasks/{id}/complete', {
      id,
    })
  },

  /**
   * @summary 【前台】公告-完成活动任务
   *  - 添加完成人到任务人员池
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  completeLiveActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(liveApi.completeLiveActivityTaskConfig(id, options)) as any
  },

  useCompleteLiveActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => liveApi.completeLiveActivityTask(id),
      mutationOptions
    )
  },

  checkLiveTaskIsSubmitConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/live/activity-tasks/{id}/is-submit', {
      id,
    })
  },

  /**
   * @summary 【后台】公告-查询是否已有学生完成
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  checkLiveTaskIsSubmit(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(liveApi.checkLiveTaskIsSubmitConfig(id, options)) as any
  },

  useCheckLiveTaskIsSubmit(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<boolean> {
    return useQueryApi<boolean>(
      liveApi.checkLiveTaskIsSubmit,
      'Live.checkLiveTaskIsSubmit',
      queryOptions,
      id
    )
  },

  exportLiveTaskReflectionsConfig(
    id: number,
    exportLiveTaskReflectionsQuery?: ExportLiveTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/live/activity-tasks/{id}/reflections/export',
      {
        id,
      },
      exportLiveTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】直播-导出任务完成数据
   * @param id
   * @param [exportLiveTaskReflectionsQuery]
   * @param [exportLiveTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [exportLiveTaskReflectionsQuery.reflection.nameIn]
   * @param [exportLiveTaskReflectionsQuery.reflection.nameLike]
   * @param [exportLiveTaskReflectionsQuery.reflection.accountLike]
   * @param [exportLiveTaskReflectionsQuery.reflection.accountIn]
   * @param [exportLiveTaskReflectionsQuery.reflection.genderIn]
   * @param [exportLiveTaskReflectionsQuery.reflection.statusIn]
   * @param [exportLiveTaskReflectionsQuery.reflection.phoneIn]
   * @param [exportLiveTaskReflectionsQuery.reflection.phoneLike]
   * @param [exportLiveTaskReflectionsQuery.reflection.emailIn]
   * @param [exportLiveTaskReflectionsQuery.reflection.emailLike]
   * @param [exportLiveTaskReflectionsQuery.endAtEgt] 任务-查看时间-大于
   * @param [exportLiveTaskReflectionsQuery.endAtElt] 任务-查看时间-小于
   * @param [exportLiveTaskReflectionsQuery.filename] 不带后缀文件名
   * @param [exportLiveTaskReflectionsQuery.ridIn]
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  exportLiveTaskReflections(
    id: number,
    exportLiveTaskReflectionsQuery?: ExportLiveTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      liveApi.exportLiveTaskReflectionsConfig(
        id,
        exportLiveTaskReflectionsQuery,
        options
      )
    ) as any
  },

  useExportLiveTaskReflections(
    {
      id,
      query,
    }: {
      id: number
      query?: ExportLiveTaskReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      liveApi.exportLiveTaskReflections,
      'Live.exportLiveTaskReflections',
      queryOptions,
      id,
      query
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(liveApi).forEach((api) => {
  // @ts-ignore
  liveApi[api]._name_ = `Live.${api}`
})
